import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { copyText } from "@/lib/clipboard";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import MicrositeRenderer from "@/components/MicrositeRenderer";
import {
  ProfilTab, TautanTab, TampilanTab,
  DEFAULT_MICROSITE_FORM, microsToForm, formToPayload,
} from "@/components/MicrositeEditorFields";
import {
  ArrowLeft, Check, Copy, ExternalLink, Loader2, QrCode, AlertCircle,
} from "lucide-react";

const TABS = [
  { id: "basic", label: "Profil" },
  { id: "links", label: "Tautan" },
  { id: "design", label: "Tampilan" },
];

// ============ PHONE FRAME PREVIEW ============
const PhoneFrame = ({ microsite }) => (
  <div className="mx-auto" style={{ width: 300 }}>
    <div
      className="rounded-[38px] p-2.5 mx-auto"
      style={{ background: "linear-gradient(160deg, #2a2c35, #101116)", boxShadow: "0 30px 60px -30px rgba(0,0,0,.6)" }}
    >
      <div className="rounded-[28px] overflow-hidden bg-background" style={{ height: 560 }}>
        <div className="h-6 flex items-center justify-center shrink-0">
          <div className="w-16 h-4 rounded-full" style={{ background: "#05050a" }} />
        </div>
        <div className="overflow-y-auto" style={{ height: "calc(100% - 24px)" }}>
          <MicrositeRenderer microsite={microsite} interactive={false} desktopFrame={false} />
        </div>
      </div>
    </div>
  </div>
);

// ============ BROWSER MOCKUP UNTUK MODE "HALAMAN PUBLIK" ============
const BrowserMockup = ({ url, microsite }) => (
  <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden border border-border shadow-2xl">
    <div className="flex items-center gap-3 px-4 py-2.5 bg-muted/60 border-b border-border">
      <span className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      </span>
      <span className="flex-1 text-center text-xs font-mono text-muted-foreground bg-background/60 py-1.5 rounded-full truncate px-3">
        {url}
      </span>
    </div>
    <div className="max-h-[70vh] overflow-y-auto bg-background">
      <MicrositeRenderer microsite={microsite} interactive={false} desktopFrame={false} />
    </div>
  </div>
);

const SAVE_LABEL = {
  idle: { text: "Tersimpan", dot: "bg-emerald-400" },
  dirty: { text: "Ada perubahan...", dot: "bg-amber-400" },
  saving: { text: "Menyimpan...", dot: "bg-amber-400 animate-pulse" },
  saved: { text: "Tersimpan otomatis", dot: "bg-emerald-400" },
  error: { text: "Gagal menyimpan", dot: "bg-red-500" },
};

const MicrositeStudioPage = () => {
  const { micrositeId } = useParams();
  const navigate = useNavigate();

  const [microsite, setMicrosite] = useState(null);
  const [form, setForm] = useState(DEFAULT_MICROSITE_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [slugStatus, setSlugStatus] = useState(null);
  const [view, setView] = useState("editor"); // editor | public
  const [activeTab, setActiveTab] = useState("basic");
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const skipNextSave = useRef(true);

  const publicUrl = microsite ? `${window.location.origin}/m/${microsite.slug}` : "";
  const previewSlug = form.custom_slug || microsite?.slug || "";

  // QR di-cache setelah dibuat sekali — reset kalau avatar atau tema berubah
  // supaya dialog QR berikutnya tidak menampilkan logo/warna yang basi.
  useEffect(() => { setQrImage(null); }, [form.avatar_url, form.theme]);

  const loadMicrosite = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/microsites/${micrositeId}`, { withCredentials: true });
      setMicrosite(res.data);
      setForm(microsToForm(res.data));
      skipNextSave.current = true;
    } catch {
      toast.error("Microsite tidak ditemukan");
      navigate("/microsites", { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [micrositeId, navigate]);

  useEffect(() => { loadMicrosite(); }, [loadMicrosite]);

  const patch = (fields) => setForm((f) => ({ ...f, ...fields }));

  // Cek ketersediaan slug — dilewati kalau sama dengan slug yang sudah aktif.
  useEffect(() => {
    if (!microsite) return;
    if (!form.custom_slug) { setSlugStatus(null); return; }
    if (form.custom_slug === microsite.slug) { setSlugStatus(null); return; }
    const delay = setTimeout(async () => {
      const slug = form.custom_slug;
      if (slug.length < 3) { setSlugStatus("too_short"); return; }
      if (!/^[a-z0-9\-]+$/.test(slug)) { setSlugStatus("invalid"); return; }
      setSlugStatus("checking");
      try {
        const res = await axios.get(`${API}/microsites/check-slug/${slug}?exclude_id=${micrositeId}`);
        setSlugStatus(res.data.available ? "available" : "taken");
      } catch {
        setSlugStatus("taken");
      }
    }, 500);
    return () => clearTimeout(delay);
  }, [form.custom_slug, microsite, micrositeId]);

  // Autosave dengan debounce — ditunda selama slug custom masih belum valid.
  useEffect(() => {
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    const slugBlocked = ["checking", "taken", "invalid", "too_short"].includes(slugStatus);
    setSaveStatus(slugBlocked ? "dirty" : "dirty");
    if (slugBlocked) return;

    const delay = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const res = await axios.patch(`${API}/microsites/${micrositeId}`, formToPayload(form), { withCredentials: true });
        setMicrosite(res.data);
        setSaveStatus("saved");
      } catch (err) {
        setSaveStatus("error");
        toast.error(err.response?.data?.detail || "Gagal menyimpan perubahan");
      }
    }, 900);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, slugStatus]);

  const handleCopy = async () => {
    const ok = await copyText(publicUrl);
    if (ok) {
      setCopied(true);
      toast.success("URL disalin");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Gagal menyalin URL");
    }
  };

  const openQr = async () => {
    setQrOpen(true);
    if (qrImage) return;
    setQrLoading(true);
    try {
      const theme = { purple: "#7c3aed", blue: "#3b82f6", green: "#10b981", pink: "#ec4899", orange: "#f59e0b", red: "#ef4444", teal: "#14b8a6", indigo: "#6366f1" };
      const res = await axios.post(`${API}/qrcode`, {
        url: publicUrl,
        fg_color: theme[form.theme] || "#7c3aed",
        bg_color: "#ffffff",
        module_style: "rounded",
        size: 480,
        logo_url: form.avatar_url || null,
      });
      setQrImage(res.data.qr_code);
    } catch {
      toast.error("Gagal membuat kode QR");
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQr = () => {
    if (!qrImage) return;
    const a = document.createElement("a");
    a.href = qrImage;
    a.download = `qr-${microsite?.slug || "microsite"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const pageScore = (() => {
    let score = 0;
    if (form.avatar_url) score += 20;
    if (form.bio.trim()) score += 15;
    if (form.links.filter(l => l.title && l.url).length > 0) score += 30;
    if (form.social_links.filter(s => s.value.trim()).length > 0) score += 15;
    if (form.custom_slug) score += 10;
    if (form.verified || form.contact_enabled) score += 10;
    return Math.min(100, score);
  })();

  const previewMicrosite = { ...form, slug: previewSlug, links: form.links.filter(l => l.title) };
  const saveLabel = SAVE_LABEL[saveStatus] || SAVE_LABEL.idle;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="microsite-studio-page">
        {/* Toolbar halaman — bukan header aplikasi, sidebar & header dashboard tetap tampil */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate("/microsites")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-bold text-xl hidden sm:block" style={{ fontFamily: "Plus Jakarta Sans" }}>
            Microsite Studio
          </h1>

          <div className="flex items-center gap-2 px-2 py-1.5 pl-3 rounded-full border border-border bg-muted/40 text-xs font-mono text-muted-foreground min-w-0">
            <span className="truncate">{window.location.host}/m/{microsite?.slug}</span>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 flex items-center gap-1 bg-muted rounded-full px-2 py-1 text-[11px] font-sans font-semibold hover:bg-muted-foreground/20 transition-colors"
            >
              {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>

          <div className="flex-1 min-w-2" />

          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border border-border">
            <button type="button" onClick={() => setView("editor")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${view === "editor" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Editor
            </button>
            <button type="button" onClick={() => setView("public")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${view === "public" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Halaman publik
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground">
              {saveStatus === "error" ? (
                <AlertCircle className="w-3 h-3 text-red-500" />
              ) : (
                <span className={`w-1.5 h-1.5 rounded-full ${saveLabel.dot}`} />
              )}
              {saveLabel.text}
            </div>
            <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, "_blank")}>
              Buka <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>

        {view === "editor" ? (
          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            <section className="min-w-0">
              <div className="flex gap-1 p-1 mb-5 rounded-lg bg-muted border border-border">
                {TABS.map(tab => (
                  <button key={tab.id} type="button"
                    className={`flex-1 py-1.5 text-sm rounded-md transition-colors ${activeTab === tab.id ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`}
                    onClick={() => setActiveTab(tab.id)}>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {activeTab === "basic" && <ProfilTab form={form} patch={patch} slugStatus={slugStatus} />}
                {activeTab === "links" && <TautanTab form={form} patch={patch} />}
                {activeTab === "design" && <TampilanTab form={form} patch={patch} />}
              </div>
            </section>

            {/* Preview sticky */}
            <aside className="hidden lg:block sticky top-[5.5rem] space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pratinjau Langsung</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={openQr}>
                    <QrCode className="w-3.5 h-3.5 mr-1" /> QR
                  </Button>
                </div>
              </div>
              <PhoneFrame microsite={previewMicrosite} />
              <div className="flex gap-2">
                <div className="flex-1 rounded-xl border border-border p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Skor Halaman</p>
                  <p className="text-lg font-bold font-mono text-emerald-500">{pageScore}/100</p>
                </div>
                <div className="flex-1 rounded-xl border border-border p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Kunjungan</p>
                  <p className="text-lg font-bold font-mono">{microsite?.views ?? 0}</p>
                </div>
              </div>
            </aside>
          </div>
        ) : (
          <div className="py-2">
            <BrowserMockup url={publicUrl} microsite={previewMicrosite} />
          </div>
        )}
      </div>

      {/* Dialog QR */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Kode QR Microsite</DialogTitle>
            <DialogDescription>Pindai untuk membuka {publicUrl}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {qrLoading ? (
              <div className="w-56 h-56 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : qrImage ? (
              <img src={qrImage} alt="QR Code" className="w-56 h-56 rounded-xl border border-border" />
            ) : null}
            <Button onClick={downloadQr} disabled={!qrImage} className="w-full">
              Download PNG
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MicrositeStudioPage;
